import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type {
  AppointmentDraftEditableResponse,
  AppointmentDraftNotEditableReason,
  AppointmentDraftResponse,
  AppointmentDraftSaveRefusalResponse,
  AppointmentDraftSaveRequest,
  AppointmentDraftSaveResponse,
  SoapDraftApproveRefusalResponse,
  SoapDraftApproveRequest,
} from "../../../../shared/api-types";
import type { RichTextDocument } from "../../../../shared/rich-text-document";
import { ApiRequestError, NetworkRequestError, authenticatedApi } from "@/lib/api";
import {
  AppointmentDraftAutosave,
  isMatchingApprovalResponse,
  isRevisionBoundDraftResponse,
  type AppointmentDraftHeadObservation,
  type AppointmentDraftAutosaveSnapshot,
  type AppointmentDraftSaveOutcome,
} from "@/lib/appointment-draft-autosave";
import {
  copyAppointmentDraftAfterHeadCheck,
  soapDraftApprovalRefusalOutcome,
  type AppointmentDraftClipboardWrite,
  type AppointmentDraftCopyOutcome,
} from "@/lib/appointment-draft-copy";
import { AppointmentDraftLoadLifecycle } from "./appointment-draft-load-lifecycle";
import {
  soapDraftPath,
  soapDraftTargetKey,
  type SoapDraftClientTarget,
} from "@/lib/soap-draft-target";
import { SoapDraftApprovalLifecycle } from "@/lib/soap-draft-approval-lifecycle";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

export type { SoapDraftClientTarget } from "@/lib/soap-draft-target";

export type SoapDraftApprovalActionOutcome =
  | "approved"
  | "conflict"
  | "refused"
  /** NO-785: a Re-enhance is making a new draft; approve that one. */
  | "re_enhancing"
  | "superseded"
  | "unavailable";

export type AppointmentDraftAutosaveUiState = Omit<
  AppointmentDraftAutosaveSnapshot,
  "document" | "persistedDraft"
>;

/**
 * What the workspace can do with this Appointment's Draft right now.
 * `unavailable` means no ready Draft revision has been announced yet;
 * `not_editable` is a Draft the editor must not mount.
 */
export type AppointmentDraftEditorState =
  | { status: "unavailable" }
  | { status: "loading" }
  | {
      status: "not_editable";
      reason: AppointmentDraftNotEditableReason;
      /**
       * Present when the Draft stopped being editable mid-session, under an
       * editor with unsaved text. The surface shows it so the RD's words are
       * never silently discarded; a fresh mount has nothing unsaved.
       */
      unsavedDocument?: RichTextDocument;
    }
  | { status: "load_failed" }
  | { status: "editable"; initialDocument: RichTextDocument };

const CLEAN_AUTOSAVE: AppointmentDraftAutosaveUiState = {
  status: "saved",
  dirty: false,
  conflict: null,
  notEditableReason: null,
};

function autosaveUiState(
  snapshot: AppointmentDraftAutosaveSnapshot
): AppointmentDraftAutosaveUiState {
  return {
    status: snapshot.status,
    dirty: snapshot.dirty,
    conflict: snapshot.conflict,
    notEditableReason: snapshot.notEditableReason,
  };
}

function sameAutosaveUiState(
  current: AppointmentDraftAutosaveUiState,
  next: AppointmentDraftAutosaveUiState
): boolean {
  if (current.status !== next.status) return false;

  if (current.dirty !== next.dirty) return false;

  if (current.conflict !== next.conflict) return false;

  return current.notEditableReason === next.notEditableReason;
}

async function readSoapDraft(
  getToken: ClerkGetToken,
  target: SoapDraftClientTarget
): Promise<AppointmentDraftResponse> {
  const response = await authenticatedApi<{ data: AppointmentDraftResponse }>(
    getToken,
    soapDraftPath(target)
  );

  if (
    response.data.status === "editable" &&
    !isRevisionBoundDraftResponse(response.data)
  ) {
    throw new Error("appointment_draft_response_invalid");
  }

  return response.data;
}

async function saveSoapDraft(
  getToken: ClerkGetToken,
  target: SoapDraftClientTarget,
  request: AppointmentDraftSaveRequest
): Promise<AppointmentDraftSaveOutcome> {
  try {
    const response = await authenticatedApi<{ data: AppointmentDraftSaveResponse }>(
      getToken,
      soapDraftPath(target),
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      }
    );

    if (!isRevisionBoundDraftResponse(response.data)) {
      throw new Error("appointment_draft_response_invalid");
    }

    return { status: "saved", value: response.data };
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 409) {
      const body = error.body as AppointmentDraftSaveRefusalResponse;

      if (body?.code === "appointment_draft_conflict") {
        if (!isRevisionBoundDraftResponse(body.current)) throw error;

        return { status: "conflict", current: body.current };
      }

      if (body?.code === "appointment_draft_not_editable") {
        return { status: "not_editable", reason: body.reason };
      }
    }

    throw error;
  }
}

async function approveSoapDraft(
  getToken: ClerkGetToken,
  target: SoapDraftClientTarget,
  request: SoapDraftApproveRequest
): Promise<AppointmentDraftEditableResponse> {
  const response = await authenticatedApi<{ data: AppointmentDraftEditableResponse }>(
    getToken,
    `${soapDraftPath(target)}/approve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    }
  );

  if (!isRevisionBoundDraftResponse(response.data)) {
    throw new Error("appointment_draft_response_invalid");
  }

  return response.data;
}

function captureDraftFailure(
  error: unknown,
  operation: "read" | "save" | "approve"
): void {
  const failure = classifyDraftFailure(error);
  const message =
    operation === "read"
      ? "appointment_draft_read_failed"
      : operation === "save"
        ? "appointment_draft_autosave_failed"
        : "soap_draft_approval_failed";
  Sentry.captureException(new Error(message), {
    tags: {
      ...failure,
      operation,
      source: "client",
      surface: "appointment_drafts",
    },
  });
}

function classifyDraftFailure(error: unknown): {
  code: "network_error" | "http_error" | "unexpected_response";
  status?: number;
} {
  if (error instanceof NetworkRequestError) return { code: "network_error" };

  if (error instanceof ApiRequestError) {
    return { code: "http_error", status: error.status };
  }

  return { code: "unexpected_response" };
}

/**
 * Loads the Draft's latest saved revision and drives autosave over it.
 *
 * The read is the reconnect path: mounting the workspace again — after
 * navigating away, after a reload, in a second tab — always starts from the
 * durable head, never from anything the browser kept. The load runs once per
 * mounted Appointment so a poll that observes our own new revision id can
 * never yank the editor out from under the RD; `reload` is the explicit way
 * back to the server's truth.
 */
export function useSoapDraftAutosave({
  target,
  ready,
  getToken,
}: {
  target: SoapDraftClientTarget;
  ready: boolean;
  getToken: ClerkGetToken;
}) {
  const draftState = useDraftState();
  const [reloadCount, setReloadCount] = useState(0);
  const [loadLifecycle] = useState(() => new AppointmentDraftLoadLifecycle());
  const [approvalLifecycle] = useState(() => new SoapDraftApprovalLifecycle());
  const controllerRef = useRef<AppointmentDraftAutosave | null>(null);
  const targetKey = soapDraftTargetKey(target);
  useDraftTargetReset(
    targetKey,
    draftState,
    loadLifecycle,
    approvalLifecycle,
    controllerRef
  );
  useDraftLoad({
    target,
    targetKey,
    ready,
    getToken,
    reloadCount,
    loadLifecycle,
    controllerRef,
    draftState,
  });
  useDraftDisposal(loadLifecycle, approvalLifecycle, controllerRef);
  const reload = useDraftReload(
    draftState,
    loadLifecycle,
    approvalLifecycle,
    controllerRef,
    setReloadCount
  );
  const actions = useDraftActions({
    target,
    targetKey,
    getToken,
    approvalLifecycle,
    controllerRef,
    draftState,
  });

  return draftAutosaveApi(draftState, actions, reload, controllerRef);
}

function draftAutosaveApi(
  draftState: ReturnType<typeof useDraftState>,
  actions: ReturnType<typeof useDraftActions>,
  reload: () => void,
  controllerRef: MutableRefObject<AppointmentDraftAutosave | null>
) {
  return {
    state: draftState.state,
    snapshot: draftState.snapshot,
    persistedDraft: draftState.persistedDraft,
    replacement: draftState.replacement,
    approving: draftState.approving,
    approveCurrent: actions.approveCurrent,
    copyCurrent: actions.copyCurrent,
    reload,
    edit: actions.edit,
    retry: () => controllerRef.current?.retry(),
    useLatest: actions.useLatest,
    saveMine: actions.saveMine,
  };
}

function useDraftState() {
  const [state, setState] = useState<AppointmentDraftEditorState>({
    status: "unavailable",
  });
  const [snapshot, setSnapshot] =
    useState<AppointmentDraftAutosaveUiState>(CLEAN_AUTOSAVE);
  const [persistedDraft, setPersistedDraft] =
    useState<AppointmentDraftEditableResponse>();
  const [replacement, setReplacement] = useState<RichTextDocument>();
  const [approving, setApproving] = useState(false);

  return {
    state,
    setState,
    snapshot,
    setSnapshot,
    persistedDraft,
    setPersistedDraft,
    replacement,
    setReplacement,
    approving,
    setApproving,
  };
}

type DraftState = ReturnType<typeof useDraftState>;
type DraftControllerRef = MutableRefObject<AppointmentDraftAutosave | null>;

function resetDraftState(draftState: DraftState): void {
  draftState.setApproving(false);
  draftState.setState({ status: "unavailable" });
  draftState.setReplacement(undefined);
  draftState.setPersistedDraft(undefined);
  draftState.setSnapshot(CLEAN_AUTOSAVE);
}

function useDraftTargetReset(
  targetKey: string,
  draftState: DraftState,
  loadLifecycle: AppointmentDraftLoadLifecycle,
  approvalLifecycle: SoapDraftApprovalLifecycle,
  controllerRef: DraftControllerRef
): void {
  useEffect(() => {
    approvalLifecycle.invalidate();
    controllerRef.current?.dispose();
    controllerRef.current = null;
    loadLifecycle.reset();
    resetDraftState(draftState);
  }, [targetKey, loadLifecycle, approvalLifecycle]);
}

function useDraftLoad(input: {
  target: SoapDraftClientTarget;
  targetKey: string;
  ready: boolean;
  getToken: ClerkGetToken;
  reloadCount: number;
  loadLifecycle: AppointmentDraftLoadLifecycle;
  controllerRef: DraftControllerRef;
  draftState: DraftState;
}): void {
  useEffect(() => {
    input.loadLifecycle.start(
      input.ready,
      () => startDraftRead(input),
      (draft) => acceptLoadedDraft(input, draft),
      (error) => rejectDraftRead(input.draftState, error)
    );
  }, [
    input.targetKey,
    input.ready,
    input.getToken,
    input.loadLifecycle,
    input.reloadCount,
  ]);
}

function startDraftRead(input: Parameters<typeof useDraftLoad>[0]) {
  input.draftState.setState({ status: "loading" });

  return readSoapDraft(input.getToken, input.target);
}

function acceptLoadedDraft(
  input: Parameters<typeof useDraftLoad>[0],
  draft: AppointmentDraftResponse
): void {
  if (draft.status === "not_editable") {
    input.draftState.setState({ status: "not_editable", reason: draft.reason });

    return;
  }

  input.controllerRef.current = createAutosave(
    draft,
    input.getToken,
    input.target,
    (full) => applyAutosaveSnapshot(input.draftState, full)
  );
  input.draftState.setPersistedDraft(draft);
  input.draftState.setSnapshot(CLEAN_AUTOSAVE);
  input.draftState.setState({ status: "editable", initialDocument: draft.document });
}

function applyAutosaveSnapshot(
  draftState: DraftState,
  full: AppointmentDraftAutosaveSnapshot
): void {
  const next = autosaveUiState(full);
  draftState.setSnapshot((current) =>
    sameAutosaveUiState(current, next) ? current : next
  );
  draftState.setPersistedDraft(full.persistedDraft);
  if (full.notEditableReason === null) return;

  draftState.setState({
    status: "not_editable",
    reason: full.notEditableReason,
    unsavedDocument: full.document,
  });
}

function rejectDraftRead(draftState: DraftState, error: unknown): void {
  captureDraftFailure(error, "read");
  draftState.setState({ status: "load_failed" });
}

function useDraftDisposal(
  loadLifecycle: AppointmentDraftLoadLifecycle,
  approvalLifecycle: SoapDraftApprovalLifecycle,
  controllerRef: DraftControllerRef
): void {
  useEffect(
    () => () => {
      approvalLifecycle.invalidate();
      loadLifecycle.dispose();
      controllerRef.current?.dispose();
    },
    [loadLifecycle, approvalLifecycle]
  );
}

function useDraftReload(
  draftState: DraftState,
  loadLifecycle: AppointmentDraftLoadLifecycle,
  approvalLifecycle: SoapDraftApprovalLifecycle,
  controllerRef: DraftControllerRef,
  setReloadCount: Dispatch<SetStateAction<number>>
): () => void {
  return useCallback(() => {
    approvalLifecycle.invalidate();
    controllerRef.current?.dispose();
    controllerRef.current = null;
    loadLifecycle.reset();
    resetDraftState(draftState);
    setReloadCount((count) => count + 1);
  }, [loadLifecycle, approvalLifecycle]);
}

interface DraftActionInput {
  target: SoapDraftClientTarget;
  targetKey: string;
  getToken: ClerkGetToken;
  approvalLifecycle: SoapDraftApprovalLifecycle;
  controllerRef: DraftControllerRef;
  draftState: DraftState;
}

function useDraftActions(input: DraftActionInput) {
  const copyCurrent = useDraftCopy(input);
  const approveCurrent = useDraftApproval(input);
  const invalidateApproval = useCallback(() => {
    input.approvalLifecycle.invalidate();
    input.draftState.setApproving(false);
  }, [input.approvalLifecycle]);
  const useLatest = useCallback(() => {
    invalidateApproval();
    const value = input.controllerRef.current?.useLatest();
    if (value) input.draftState.setReplacement(value);
  }, [invalidateApproval]);
  const edit = useCallback(
    (document: RichTextDocument) => {
      invalidateApproval();
      input.controllerRef.current?.edit(document);
    },
    [invalidateApproval]
  );
  const saveMine = useCallback(() => {
    invalidateApproval();
    input.controllerRef.current?.saveMine();
  }, [invalidateApproval]);

  return { approveCurrent, copyCurrent, edit, saveMine, useLatest };
}

function useDraftCopy(input: DraftActionInput) {
  return useCallback(
    async (
      clipboard: AppointmentDraftClipboardWrite
    ): Promise<AppointmentDraftCopyOutcome> => {
      input.approvalLifecycle.invalidate();
      input.draftState.setApproving(false);

      return copyAppointmentDraftAfterHeadCheck({
        readCurrent: () => readCurrentDraft(input.getToken, input.target),
        observeCurrent: (current) => observeDraftHead(input.controllerRef, current),
        clipboard,
      });
    },
    [input.targetKey, input.getToken, input.approvalLifecycle]
  );
}

async function readCurrentDraft(
  getToken: ClerkGetToken,
  target: SoapDraftClientTarget
): Promise<AppointmentDraftResponse | null> {
  try {
    return await readSoapDraft(getToken, target);
  } catch (error) {
    captureDraftFailure(error, "read");

    return null;
  }
}

function observeDraftHead(
  controllerRef: DraftControllerRef,
  current: AppointmentDraftEditableResponse
): AppointmentDraftHeadObservation {
  if (!controllerRef.current) return "unavailable";

  return controllerRef.current.observeServerHead(current);
}

function useDraftApproval(input: DraftActionInput) {
  return useCallback(
    () => runDraftApproval(input),
    [input.targetKey, input.getToken, input.approvalLifecycle]
  );
}

async function runDraftApproval(
  input: DraftActionInput
): Promise<SoapDraftApprovalActionOutcome> {
  const controller = input.controllerRef.current;
  if (!controller) return "unavailable";

  const before = controller.snapshot();
  if (!canApproveDraft(before)) return "unavailable";

  const revisionId = before.persistedDraft.revision.id;
  input.draftState.setApproving(true);
  const execution = await input.approvalLifecycle.run(() =>
    approveSoapDraft(input.getToken, input.target, { draftRevisionId: revisionId })
  );
  if (execution.status === "superseded") return "superseded";

  try {
    if (execution.status === "rejected") throw execution.error;

    return acceptDraftApproval(controller, execution.value, revisionId);
  } catch (error) {
    return draftApprovalErrorOutcome(controller, error);
  } finally {
    input.draftState.setApproving(false);
  }
}

function canApproveDraft(snapshot: AppointmentDraftAutosaveSnapshot): boolean {
  if (snapshot.status !== "saved") return false;

  if (snapshot.dirty) return false;

  if (snapshot.persistedDraft.review.state === "approved") return false;

  return snapshot.persistedDraft.review.approval === "available";
}

function acceptDraftApproval(
  controller: AppointmentDraftAutosave,
  approved: AppointmentDraftEditableResponse,
  revisionId: string
): SoapDraftApprovalActionOutcome {
  const current = controller.snapshot();
  const matches = isMatchingApprovalResponse(
    approved,
    revisionId,
    current.persistedDraft.revision.id,
    current.dirty
  );
  if (!matches) return "superseded";

  controller.observeServerHead(approved);

  return "approved";
}

function draftApprovalErrorOutcome(
  controller: AppointmentDraftAutosave,
  error: unknown
): SoapDraftApprovalActionOutcome {
  if (!(error instanceof ApiRequestError) || error.status !== 409) {
    captureDraftFailure(error, "approve");

    return "unavailable";
  }

  const body = error.body as SoapDraftApproveRefusalResponse;

  if (body?.code === "soap_draft_approval_conflict") {
    if (body.current.status === "editable") controller.observeServerHead(body.current);

    return "conflict";
  }

  const refusal = soapDraftApprovalRefusalOutcome(body);
  if (refusal) return refusal;

  captureDraftFailure(error, "approve");

  return "unavailable";
}

export function useAppointmentDraftAutosave({
  appointmentId,
  draftRevisionId,
  getToken,
}: {
  appointmentId: string;
  draftRevisionId: string | null;
  getToken: ClerkGetToken;
}) {
  return useSoapDraftAutosave({
    target: { kind: "appointment", id: appointmentId },
    ready: draftRevisionId !== null,
    getToken,
  });
}

function createAutosave(
  initial: AppointmentDraftEditableResponse,
  getToken: ClerkGetToken,
  target: SoapDraftClientTarget,
  onSnapshot: (next: AppointmentDraftAutosaveSnapshot) => void
): AppointmentDraftAutosave {
  return new AppointmentDraftAutosave(
    initial,
    (request) => saveSoapDraft(getToken, target, request),
    onSnapshot,
    { onError: (error) => captureDraftFailure(error, "save") }
  );
}
